import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Icons } from "@/components/icons";
import { toast } from "sonner";

export default function Settings() {
  const [settings, setSettings] = useState({
    // Profile Settings
    fullName: "Hassan Al-Mahmoud",
    email: "hassan.collector@taawun.qa",
    phone: "+974 5555 1234",
    collectionArea: "Doha Central District",
    bio: "Experienced HAF collector with 5+ years in community fundraising.",

    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    dailyReports: true,

    // Collection Settings
    autoReceiptGeneration: true,
    requireDonorInfo: true,
    minimumAmount: 100,
    preferredCurrency: "QAR",
  });

  const handleSave = () => {
    // Simulate saving
    toast.success("Settings saved successfully!");
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Settings</h2>
        <p className="text-muted-foreground">
          Manage your account preferences and collection settings
        </p>
      </div>

      <div className="grid gap-6">
        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>
              Update your personal information and collection area
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={settings.fullName}
                  onChange={(e) =>
                    handleInputChange("fullName", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={settings.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="collectionArea">Collection Area</Label>
                <Input
                  id="collectionArea"
                  value={settings.collectionArea}
                  onChange={(e) =>
                    handleInputChange("collectionArea", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about your experience..."
                value={settings.bio}
                onChange={(e) => handleInputChange("bio", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Preferences</CardTitle>
            <CardDescription>
              Choose how you want to receive notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive payment updates via email
                </p>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={(checked) =>
                  handleInputChange("emailNotifications", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive urgent alerts via SMS
                </p>
              </div>
              <Switch
                checked={settings.smsNotifications}
                onCheckedChange={(checked) =>
                  handleInputChange("smsNotifications", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Push Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive real-time notifications in browser
                </p>
              </div>
              <Switch
                checked={settings.pushNotifications}
                onCheckedChange={(checked) =>
                  handleInputChange("pushNotifications", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Daily Reports</Label>
                <p className="text-sm text-muted-foreground">
                  Receive daily collection summaries
                </p>
              </div>
              <Switch
                checked={settings.dailyReports}
                onCheckedChange={(checked) =>
                  handleInputChange("dailyReports", checked)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Collection Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Collection Settings</CardTitle>
            <CardDescription>
              Configure your payment collection preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto Receipt Generation</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically generate receipts for completed payments
                </p>
              </div>
              <Switch
                checked={settings.autoReceiptGeneration}
                onCheckedChange={(checked) =>
                  handleInputChange("autoReceiptGeneration", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Require Donor Information</Label>
                <p className="text-sm text-muted-foreground">
                  Require complete donor details for all payments
                </p>
              </div>
              <Switch
                checked={settings.requireDonorInfo}
                onCheckedChange={(checked) =>
                  handleInputChange("requireDonorInfo", checked)
                }
              />
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="minimumAmount">Minimum Payment Amount</Label>
                <Input
                  id="minimumAmount"
                  type="number"
                  value={settings.minimumAmount}
                  onChange={(e) =>
                    handleInputChange("minimumAmount", parseInt(e.target.value))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preferredCurrency">Preferred Currency</Label>
                <Input
                  id="preferredCurrency"
                  value={settings.preferredCurrency}
                  onChange={(e) =>
                    handleInputChange("preferredCurrency", e.target.value)
                  }
                  disabled
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
            <CardDescription>
              Manage your account security settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full">
              <Icons.key className="mr-2 h-4 w-4" />
              Change Password
            </Button>

            <Button variant="outline" className="w-full">
              <Icons.smartphone className="mr-2 h-4 w-4" />
              Setup Two-Factor Authentication
            </Button>

            <Button variant="outline" className="w-full">
              <Icons.download className="mr-2 h-4 w-4" />
              Download Account Data
            </Button>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} className="w-32">
            <Icons.save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
